import React from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import notificationService from './notificationService';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      retryCount: 0 
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    this.setState({
      error: error,
      errorInfo: errorInfo
    });

    // Notifier l'utilisateur
    notificationService.error(
      'Une erreur inattendue s\'est produite. L\'application va se récupérer automatiquement.',
      10000
    );

    // Log pour debugging
    if (process.env.NODE_ENV === 'development') {
      console.group('ErrorBoundary Debug Info');
      console.error('Error:', error);
      console.error('Error Info:', errorInfo);
      console.error('Component Stack:', errorInfo.componentStack);
      console.groupEnd();
    }
  }

  handleRetry = () => {
    this.setState(prevState => ({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: prevState.retryCount + 1
    }));
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="error-boundary">
          <div className="error-content">
            <AlertTriangle size={64} className="error-icon" />
            <h1>Oups ! Quelque chose s'est mal passé</h1>
            
            <div className="error-details">
              <p>
                L'application a rencontré une erreur inattendue. 
                {this.state.retryCount < 3 ? ' Vous pouvez essayer de récupérer.' : ' Veuillez recharger la page.'}
              </p>
              
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <details className="error-debug">
                  <summary>Détails techniques (développement)</summary>
                  <pre>{this.state.error.toString()}</pre>
                  <pre>{this.state.errorInfo.componentStack}</pre>
                </details>
              )}
            </div>

            <div className="error-actions">
              {this.state.retryCount < 3 ? (
                <button onClick={this.handleRetry} className="retry-button">
                  <RefreshCw size={16} />
                  Récupérer ({3 - this.state.retryCount} tentatives restantes)
                </button>
              ) : (
                <button onClick={() => window.location.reload()} className="reload-button">
                  <RefreshCw size={16} />
                  Recharger la page
                </button>
              )}
              
              <button onClick={this.handleGoHome} className="home-button">
                <Home size={16} />
                Retour à l'accueil
              </button>
            </div>
          </div>

          <style jsx>{`
            .error-boundary {
              display: flex;
              align-items: center;
              justify-content: center;
              min-height: 100vh;
              background: linear-gradient(135deg, var(--color-accent-400, #8B5CF6) 0%, var(--color-accent-600, #6D28D9) 100%);
              padding: 20px;
            }
            
            .error-content {
              background: white;
              border-radius: 12px;
              padding: 40px;
              text-align: center;
              box-shadow: 0 20px 40px rgba(0,0,0,0.1);
              max-width: 600px;
              width: 100%;
            }
            
            .error-icon {
              color: #ef4444;
              margin-bottom: 20px;
            }
            
            .error-content h1 {
              color: #1f2937;
              margin-bottom: 20px;
              font-size: 24px;
            }
            
            .error-details {
              margin-bottom: 30px;
            }
            
            .error-details p {
              color: #6b7280;
              line-height: 1.6;
            }
            
            .error-debug {
              margin-top: 20px;
              text-align: left;
            }
            
            .error-debug summary {
              cursor: pointer;
              color: #374151;
              font-weight: 500;
              margin-bottom: 10px;
            }
            
            .error-debug pre {
              background: #f3f4f6;
              padding: 15px;
              border-radius: 6px;
              font-size: 12px;
              overflow-x: auto;
              color: #dc2626;
            }
            
            .error-actions {
              display: flex;
              gap: 15px;
              justify-content: center;
              flex-wrap: wrap;
            }
            
            .error-actions button {
              display: flex;
              align-items: center;
              gap: 8px;
              padding: 12px 24px;
              border: none;
              border-radius: 8px;
              font-weight: 500;
              cursor: pointer;
              transition: all 0.2s;
            }
            
            .retry-button {
              background: #8B5CF6;
              color: white;
            }
            
            .retry-button:hover {
              background: #7C3AED;
            }
            
            .reload-button {
              background: #10b981;
              color: white;
            }
            
            .reload-button:hover {
              background: #059669;
            }
            
            .home-button {
              background: #6b7280;
              color: white;
            }
            
            .home-button:hover {
              background: #4b5563;
            }
          `}</style>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
